/**
 * 密码输入框组件 JavaScript
 * 实现特定的密码显示/隐藏规则：
 * 1. 默认没有小眼睛
 * 2. 聚焦后有小眼睛（close-eye）
 * 3. 点击close-eye可以变成open-eye
 * 4. 点击输入框外部如果error就显示error-icon，否则不显示小眼睛icon
 * 
 * 注意：此组件已独立实现 XSS 和 SQL 注入检测功能，不依赖 component-text-input.js
 * 如果 component-text-input.js 已加载，会优先使用其检测函数以保持一致性
 */

// 从 component-text-input.js 导入 XSS 检测函数（如果可用）
// 避免重复声明：创建一个函数来获取 detectXSS，而不是声明全局变量
(function() {
  // 创建一个局部函数来获取 detectXSS，避免全局作用域冲突
  function getDetectXSS() {
    // 优先检查 window.TextInputComponent.detectXSS（最可靠的方式）
    if (typeof window.TextInputComponent !== 'undefined' && typeof window.TextInputComponent.detectXSS === 'function') {
      return window.TextInputComponent.detectXSS;
    }
    
    // 检查全局作用域是否已经存在 detectXSS 函数（由 component-text-input.js 创建）
    // function 声明会在全局作用域创建变量，等同于 window.detectXSS
    if (typeof window.detectXSS === 'function') {
      return window.detectXSS;
    }
    
    // 如果都不存在，创建一个新的函数
    return function(input) {
      if (!input || typeof input !== 'string') {
        return false;
      }
      
      // XSS 攻击模式检测
      const xssPatterns = [
        // Script 标签
        /<script[\s\S]*?>[\s\S]*?<\/script>/gi,
        /<script[\s\S]*?>/gi,
        
        // JavaScript 协议
        /javascript:/gi,
        /jscript:/gi,
        /vbscript:/gi,
        /data:text\/html/gi,
        /data:image\/svg\+xml/gi,
        
        // 事件处理器
        /on\w+\s*=/gi,  // onclick=, onerror=, onload= 等
        /on\w+\s*:/gi,  // onclick:, onerror: 等
        
        // 危险函数调用
        /\beval\s*\(/gi,
        /\bsetTimeout\s*\(/gi,
        /\bsetInterval\s*\(/gi,
        /\bFunction\s*\(/gi,
        /\bexecScript\s*\(/gi,
        
        // 危险标签
        /<iframe[\s\S]*?>/gi,
        /<object[\s\S]*?>/gi,
        /<embed[\s\S]*?>/gi,
        /<link[\s\S]*?>/gi,
        /<meta[\s\S]*?>/gi,
        /<style[\s\S]*?>[\s\S]*?<\/style>/gi,
        
        // 编码的脚本（常见编码方式）
        /&#x3c;script/gi,  // HTML 实体编码
        /&#60;script/gi,
        /%3cscript/gi,     // URL 编码
        /%3Cscript/gi,
        
        // Base64 编码的 data URI（可能包含脚本）
        /data:[\w\/\+]+;base64,[\w\+\/=]+/gi,
        
        // 表达式注入（IE）
        /expression\s*\(/gi,
        
        // 其他危险模式
        /<img[\s\S]*?src[\s\S]*?=/gi,
        /<svg[\s\S]*?onload/gi,
        /<body[\s\S]*?onload/gi,
      ];

      // 检测是否匹配任何 XSS 模式
      for (let pattern of xssPatterns) {
        if (pattern.test(input)) {
          return true;
        }
      }

      return false;
    };
  }
  
  // 在文件作用域内使用 detectXSS，而不是全局作用域
  // 通过闭包访问，避免全局变量冲突
  const detectXSS = getDetectXSS();
  
  // 将所有使用 detectXSS 的地方都放在这个 IIFE 内部
  // 但由于函数定义在外部，我们需要将 detectXSS 作为参数传递
  // 更好的方法：将所有代码包装在 IIFE 中，或者使用函数引用
  
  // 将 detectXSS 函数保存到模块作用域，供后续函数使用
  window.__passwordInputDetectXSS = detectXSS;
})();

// SQL 注入检测函数（独立实现，不依赖 component-text-input.js）
// 检测输入内容是否包含 SQL 注入攻击模式
// 注意：前端检测只能作为第一道防线，真正的防护必须在后端进行
(function() {
  // 创建一个局部函数来获取 detectSQLInjection
  function getDetectSQLInjection() {
    // 优先检查 window.TextInputComponent.detectSQLInjection（如果 component-text-input.js 已加载）
    if (typeof window.TextInputComponent !== 'undefined' && typeof window.TextInputComponent.detectSQLInjection === 'function') {
      return window.TextInputComponent.detectSQLInjection;
    }
    
    // 如果不存在，使用内联实现
    return function(input) {
      if (!input || typeof input !== 'string') {
        return false;
      }
      
      // 转换为小写进行检测（不区分大小写）
      const lowerInput = input.toLowerCase();
      
      // SQL 注入攻击模式检测（更精确的模式，减少误报）
      const sqlPatterns = [
        // UNION SELECT 注入（最常见的 SQL 注入模式）
        /\bunion\s+[\w\s,]*\bselect\b/gi,
        
        // 布尔盲注：OR/AND 1=1, OR/AND 'a'='a' 等
        /\b(or|and)\s+['"]?\d+['"]?\s*=\s*['"]?\d+['"]?/gi,
        /\b(or|and)\s+['"][\w]+['"]\s*=\s*['"][\w]+['"]/gi,
        /\b(or|and)\s+['"]?1['"]?\s*=\s*['"]?1['"]?/gi,
        
        // SQL 注释符与关键字的组合（可疑模式）
        /(--|\#|\/\*)[\s]*\b(select|insert|update|delete|drop|create|alter|exec|execute|union)\b/gi,
        
        // 堆叠查询：分号后跟 SQL 语句
        /;[\s]*\b(select|insert|update|delete|drop|create|alter|exec|execute|union)\b/gi,
        
        // 时间盲注：SLEEP、WAITFOR、BENCHMARK 函数
        /\b(sleep|waitfor|benchmark)\s*\(/gi,
        
        // 文件操作注入：LOAD_FILE、INTO OUTFILE 等
        /\b(load_file|into\s+outfile|into\s+dumpfile)\s*\(/gi,
        
        // 单引号转义尝试：' OR '1'='1, '; DROP TABLE, '-- 等
        /'[\s]*(or|and|;|--|\/\*)/gi,
        
        // 双引号转义尝试
        /"[\s]*(or|and|;|--|\/\*)/gi,
        
        // 编码的 SQL 注入（URL 编码）
        /(%27|%22)[\s]*(%4f%52|%41%4e%44|%3b|%2d%2d)/gi,  // ' OR, " AND, ;, --
        
        // 十六进制编码的 SQL 关键字
        /0x[0-9a-f]*(53454c454354|494e53455254|555044415445|44454c455445)/gi,  // SELECT, INSERT, UPDATE, DELETE
        
        // 双重编码尝试
        /%25[0-9a-f]{2}/gi,  // %25 是 % 的编码，表示双重编码
      ];

      // 检测是否匹配任何 SQL 注入模式
      for (let pattern of sqlPatterns) {
        if (pattern.test(lowerInput)) {
          return true;
        }
      }

      return false;
    };
  }
  
  // 将 detectSQLInjection 函数保存到模块作用域，供后续函数使用
  const detectSQLInjection = getDetectSQLInjection();
  window.__passwordInputDetectSQLInjection = detectSQLInjection;
})();

// 创建一个安全的 detectXSS 访问函数，使用不同的函数名避免全局冲突
// 注意：这个函数名不会与 component-text-input.js 中的 detectXSS 冲突
function passwordInputDetectXSS(input) {
  // 优先使用已保存的函数
  if (typeof window.__passwordInputDetectXSS === 'function') {
    return window.__passwordInputDetectXSS(input);
  }
  
  // 回退到 TextInputComponent
  if (typeof window.TextInputComponent !== 'undefined' && typeof window.TextInputComponent.detectXSS === 'function') {
    return window.TextInputComponent.detectXSS(input);
  }
  
  // 回退到全局 detectXSS（如果存在）
  if (typeof window.detectXSS === 'function') {
    return window.detectXSS(input);
  }
  
  // 最后的回退：内联实现
  if (!input || typeof input !== 'string') {
    return false;
  }
  
  const xssPatterns = [
    /<script[\s\S]*?>[\s\S]*?<\/script>/gi,
    /<script[\s\S]*?>/gi,
    /javascript:/gi,
    /jscript:/gi,
    /vbscript:/gi,
    /data:text\/html/gi,
    /data:image\/svg\+xml/gi,
    /on\w+\s*=/gi,
    /on\w+\s*:/gi,
    /\beval\s*\(/gi,
    /\bsetTimeout\s*\(/gi,
    /\bsetInterval\s*\(/gi,
    /\bFunction\s*\(/gi,
    /\bexecScript\s*\(/gi,
    /<iframe[\s\S]*?>/gi,
    /<object[\s\S]*?>/gi,
    /<embed[\s\S]*?>/gi,
    /<link[\s\S]*?>/gi,
    /<meta[\s\S]*?>/gi,
    /<style[\s\S]*?>[\s\S]*?<\/style>/gi,
    /&#x3c;script/gi,
    /&#60;script/gi,
    /%3cscript/gi,
    /%3Cscript/gi,
    /data:[\w\/\+]+;base64,[\w\+\/=]+/gi,
    /expression\s*\(/gi,
    /<img[\s\S]*?src[\s\S]*?=/gi,
    /<svg[\s\S]*?onload/gi,
    /<body[\s\S]*?onload/gi,
  ];
  
  for (let pattern of xssPatterns) {
    if (pattern.test(input)) {
      return true;
    }
  }
  
  return false;
}

// 创建一个安全的 detectSQLInjection 访问函数
function passwordInputDetectSQLInjection(input) {
  // 优先使用已保存的函数
  if (typeof window.__passwordInputDetectSQLInjection === 'function') {
    return window.__passwordInputDetectSQLInjection(input);
  }
  
  // 回退到 TextInputComponent（如果 component-text-input.js 已加载）
  if (typeof window.TextInputComponent !== 'undefined' && typeof window.TextInputComponent.detectSQLInjection === 'function') {
    return window.TextInputComponent.detectSQLInjection(input);
  }
  
  // 最后的回退：内联实现
  if (!input || typeof input !== 'string') {
    return false;
  }
  
  // 转换为小写进行检测（不区分大小写）
  const lowerInput = input.toLowerCase();
  
  // SQL 注入攻击模式检测（更精确的模式，减少误报）
  const sqlPatterns = [
    // UNION SELECT 注入（最常见的 SQL 注入模式）
    /\bunion\s+[\w\s,]*\bselect\b/gi,
    
    // 布尔盲注：OR/AND 1=1, OR/AND 'a'='a' 等
    /\b(or|and)\s+['"]?\d+['"]?\s*=\s*['"]?\d+['"]?/gi,
    /\b(or|and)\s+['"][\w]+['"]\s*=\s*['"][\w]+['"]/gi,
    /\b(or|and)\s+['"]?1['"]?\s*=\s*['"]?1['"]?/gi,
    
    // SQL 注释符与关键字的组合（可疑模式）
    /(--|\#|\/\*)[\s]*\b(select|insert|update|delete|drop|create|alter|exec|execute|union)\b/gi,
    
    // 堆叠查询：分号后跟 SQL 语句
    /;[\s]*\b(select|insert|update|delete|drop|create|alter|exec|execute|union)\b/gi,
    
    // 时间盲注：SLEEP、WAITFOR、BENCHMARK 函数
    /\b(sleep|waitfor|benchmark)\s*\(/gi,
    
    // 文件操作注入：LOAD_FILE、INTO OUTFILE 等
    /\b(load_file|into\s+outfile|into\s+dumpfile)\s*\(/gi,
    
    // 单引号转义尝试：' OR '1'='1, '; DROP TABLE, '-- 等
    /'[\s]*(or|and|;|--|\/\*)/gi,
    
    // 双引号转义尝试
    /"[\s]*(or|and|;|--|\/\*)/gi,
    
    // 编码的 SQL 注入（URL 编码）
    /(%27|%22)[\s]*(%4f%52|%41%4e%44|%3b|%2d%2d)/gi,  // ' OR, " AND, ;, --
    
    // 十六进制编码的 SQL 关键字
    /0x[0-9a-f]*(53454c454354|494e53455254|555044415445|44454c455445)/gi,  // SELECT, INSERT, UPDATE, DELETE
    
    // 双重编码尝试
    /%25[0-9a-f]{2}/gi,  // %25 是 % 的编码，表示双重编码
  ];
  
  // 检测是否匹配任何 SQL 注入模式
  for (let pattern of sqlPatterns) {
    if (pattern.test(lowerInput)) {
      return true;
    }
  }
  
  return false;
}

// 注意：文件内所有使用 detectXSS 的地方都应该使用 passwordInputDetectXSS
// 文件内所有使用 detectSQLInjection 的地方都应该使用 passwordInputDetectSQLInjection
// 这样可以避免与 component-text-input.js 中的全局函数冲突

document.addEventListener('DOMContentLoaded', function() {
  initPasswordInputs();
});

function initPasswordInputs() {
  // 根据当前设备类型选择正确的布局
  const isMobile = window.innerWidth <= 734;
  const targetLayout = isMobile ? '.root-mobile-layout' : '.root-desktop-layout';
  
  // 在指定的布局中查找元素
  const layout = document.querySelector(targetLayout);
  if (!layout) {
    return;
  }
  
  const passwordInputs = layout.querySelectorAll('[data-component="password-input"]');
  
  passwordInputs.forEach((input, index) => {
    const field = input.querySelector('.component-password-input__field');
    const label = input.querySelector('.component-password-input__label');
    const toggle = input.querySelector('.component-password-input__toggle');
    const errorIcon = input.querySelector('.component-password-input__error-icon');
    
    if (!field) return;
    
    // 初始化状态
    field.classList.remove('component-password-input__field--error');
    field.classList.remove('component-password-input__field--has-value');
    
    updatePasswordInputState(field, label, toggle, errorIcon);
    syncPasswordToggleIcon(field, toggle);

    if (typeof MutationObserver !== 'undefined') {
      const typeObserver = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'type') {
            syncPasswordToggleIcon(field, toggle);
          }
        });
      });
      typeObserver.observe(field, { attributes: true, attributeFilter: ['type'] });
    }

    function syncPasswordVisibilityState() {
      syncPasswordToggleIcon(field, toggle);
    }
    
    field.addEventListener('paste', function() {});

    field.addEventListener('compositionstart', function() {
      field.dataset.isImeComposing = 'true';
    });

    field.addEventListener('compositionend', function() {
      field.dataset.isImeComposing = 'false';
      setTimeout(function() {
        field.dispatchEvent(new Event('input', { bubbles: true }));
      }, 0);
    });
    
    // 输入事件
    field.addEventListener('input', function(e) {
      if (e && e.isComposing) {
        return;
      }

      if (field.dataset.isImeComposing === 'true') {
        return;
      }

      const hasNoValidation = input.classList.contains('no-validation') || input.classList.contains('NoValidation');
      const currentValue = field.value;

      let nextValue = currentValue.replace(/\s/g, '');
      if (!hasNoValidation) {
        nextValue = nextValue.replace(/[^a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/g, '');
      }
      if (currentValue !== nextValue) {
        field.value = nextValue;
      }
      
      // 检查最大长度限制（默认40字符）
      const maxLength = field.getAttribute('maxlength') || '40';
      if (field.value.length > parseInt(maxLength)) {
        field.value = field.value.substring(0, parseInt(maxLength));
        return;
      }
      
      // 输入时移除错误状态 - 只影响当前输入框
      field.classList.remove('component-password-input__field--error');
      
      // 调用全局错误隐藏函数 - 只影响当前输入框
      if (window.hideTextInputError) {
        window.hideTextInputError('.component-password-input__field');
      }
      
      // 清除错误信息显示 - 只影响当前输入框
      const errorElement = input.querySelector('.component-password-input__error');
      if (errorElement) {
        errorElement.style.display = 'none';
        errorElement.textContent = ''; // 清空错误文本内容
      }
      
      // 移除 aria-invalid 属性
      field.removeAttribute('aria-invalid');
      
      // 隐藏错误图标
      if (errorIcon) {
        errorIcon.style.display = 'none';
      }
      
      // 确保在更新状态之前错误状态已被清除
      setTimeout(() => {
        updatePasswordInputState(field, label, toggle, errorIcon);
        syncPasswordVisibilityState();
      }, 0);
      
      // 实时验证密码要求（只对主密码字段，跳过no-validation类）
      const isConfirmPassword = isConfirmPasswordField(input, field);
      if (!isConfirmPassword && !hasNoValidation) {
        // 无论是否有值，都更新提示状态（空值时重置到初始状态）
        validatePasswordRequirements(field, false); // 添加参数表示不设置错误状态
      }
      
      // 完全独立：不处理其他输入框的验证
    });
    
    // 聚焦事件
    field.addEventListener('focus', function() {
      // 聚焦时移除错误状态 - 只影响当前输入框
      field.classList.remove('component-password-input__field--error');
      
      // 调用全局错误隐藏函数 - 只影响当前输入框
      if (window.hideTextInputError) {
        window.hideTextInputError('.component-password-input__field');
      }
      
      handlePasswordFocus(field, label, toggle, errorIcon);
      syncPasswordVisibilityState();
      requestAnimationFrame(syncPasswordVisibilityState);
      setTimeout(syncPasswordVisibilityState, 80);
      setTimeout(syncPasswordVisibilityState, 240);
    });
    
    // 失焦事件
    field.addEventListener('blur', function(e) {
      // 检查是否点击了密码切换按钮
      const relatedTarget = e.relatedTarget;
      const isClickingToggle = relatedTarget && relatedTarget.closest('.component-password-input__toggle');
      
      if (isClickingToggle) {
        return; // 不处理失焦逻辑
      }
      
      // 检查是否是确认密码字段 - 使用通用方法
      const isConfirmPassword = isConfirmPasswordField(input, field);
      
      if (isConfirmPassword) {
        handleConfirmPasswordBlur(field, label, toggle, errorIcon);
      } else {
        handlePasswordBlur(field, label, toggle, errorIcon);
      }
      syncPasswordVisibilityState();
    });

    field.addEventListener('change', syncPasswordVisibilityState);
    field.addEventListener('keyup', syncPasswordVisibilityState);
    
    // 添加软键盘确认键处理（Enter 键失焦）+ 禁止空格输入
    let isEnterSubmitting = false;
    field.addEventListener('keydown', function(e) {
      // Enter 键处理
      if (e.key === 'Enter' || e.keyCode === 13) {
        e.preventDefault();
        e.stopPropagation();
        
        // 防止重复提交
        if (isEnterSubmitting) {
          return;
        }
        
        // 触发失焦（失焦时会进行验证）
        this.blur();
        
        // 如果密码输入框在表单中，延迟触发表单提交
        const form = this.closest('form');
        if (form) {
          // 防止重复提交：检查表单是否正在提交
          if (form.dataset.submitting === 'true') {
            return;
          }
          
          // 设置提交标记
          isEnterSubmitting = true;
          form.dataset.submitting = 'true';
          
          // 使用 setTimeout 确保 blur 事件先处理完成
          setTimeout(function() {
            // 检查输入框是否还有错误状态，如果有错误则不提交
            const hasError = field.classList.contains('component-password-input__field--error');
            if (!hasError) { 
              // 触发表单提交
              form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
            }
            
            // 清除提交标记（延迟清除，确保提交完成）
            setTimeout(function() {
              delete form.dataset.submitting;
              isEnterSubmitting = false;
            }, 1000);
          }, 10);
        }
        
        // 触发自定义事件，让页面可以监听
        const enterEvent = new CustomEvent('passwordInputEnter', {
          detail: {
            input: this,
            value: this.value,
            component: input
          }
        });
        input.dispatchEvent(enterEvent);
      }
    });
    
    // 密码切换按钮事件
    if (toggle) {
      const isIOS = (function() {
        const platform = navigator.platform || '';
        const ua = navigator.userAgent || '';
        const isAppleMobile = /iPad|iPhone|iPod/.test(platform);
        const isTouchMac = platform === 'MacIntel' && navigator.maxTouchPoints > 1;
        return isAppleMobile || isTouchMac || /iPad|iPhone|iPod/.test(ua);
      })();

      const togglePasswordVisibility = function() {
        let start = null;
        let end = null;

        try {
          start = field.selectionStart;
          end = field.selectionEnd;
        } catch (err) {}

        const isCurrentlyPassword = field.type === 'password';
        const nextType = isCurrentlyPassword ? 'text' : 'password';

        try {
          field.setAttribute('type', nextType);
        } catch (err) {}
        field.type = nextType;

        if (isIOS) {
          const v = field.value;
          field.value = '';
          field.value = v;
        }

        syncPasswordToggleIcon(field, toggle);

        try {
          field.focus();
          if (typeof start === 'number' && typeof end === 'number') {
            field.setSelectionRange(start, end);
          }
        } catch (err) {}

        requestAnimationFrame(function() {
          syncPasswordToggleIcon(field, toggle);
        });
      };

      // 添加 mousedown 事件确保按钮获得焦点
      toggle.addEventListener('mousedown', function(e) {
        e.preventDefault();
        if (document.activeElement === toggle) {
          toggle.blur();
        }
      });
      
      toggle.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        if (field.dataset.isImeComposing === 'true') {
          field.blur();
          setTimeout(function() {
            togglePasswordVisibility();
          }, 0);
          return;
        }

        togglePasswordVisibility();

        if (document.activeElement === toggle) {
          toggle.blur();
        }
      });
    }
    
    // 电脑端自动聚焦功能（仅桌面端，不包括平板和手机）
    const shouldAutofocusDesktop = input.hasAttribute('data-autofocus-desktop') || 
                                    input.getAttribute('data-autofocus-desktop') === 'true';
    // 只在真正的桌面端（>1068px）触发自动聚焦，平板（735-1068px）和手机（<=734px）都不触发
    const isDesktop = window.innerWidth > 1068;
    if (shouldAutofocusDesktop && isDesktop) {
      // 延迟聚焦，确保页面完全加载
      setTimeout(() => {
        field.focus();
      }, 100);
    }
  });
}

/**
 * 更新密码输入框状态
 */
function updatePasswordInputState(field, label, toggle, errorIcon) {
  const value = field.value.trim();
  const hasValue = value.length > 0;
  const isFocused = field === document.activeElement;
  const hasError = field.classList.contains('component-password-input__field--error');
  
  // 更新输入框的has-value类
  if (hasValue) {
    field.classList.add('component-password-input__field--has-value');
  } else {
    field.classList.remove('component-password-input__field--has-value');
  }
  
  // 更新 label 状态
  updatePasswordLabelState(field, label, hasValue, isFocused);
  
  // 更新图标显示状态
  updatePasswordIconVisibility(field, toggle, errorIcon, hasValue, hasError, isFocused);
}

/**
 * 根据当前输入框 type 同步密码可见性图标
 */
function syncPasswordToggleIcon(field, toggle) {
  if (!field || !toggle) return;

  const eyeIcon = toggle.querySelector('.component-password-input__icon--eye');
  const eyeSlashIcon = toggle.querySelector('.component-password-input__icon--eye-slash');
  const isPasswordHidden = field.type === 'password';
  const isPasswordVisible = !isPasswordHidden;

  field.setAttribute('data-password-visible', isPasswordVisible ? 'true' : 'false');
  field.classList.toggle('component-password-input__field--visible', isPasswordVisible);
  field.classList.toggle('component-password-input__field--hidden', isPasswordHidden);

  if (eyeIcon) {
    eyeIcon.style.display = isPasswordHidden ? 'none' : 'block';
    eyeIcon.setAttribute('aria-hidden', isPasswordHidden ? 'true' : 'false');
  }

  if (eyeSlashIcon) {
    eyeSlashIcon.style.display = isPasswordHidden ? 'block' : 'none';
    eyeSlashIcon.setAttribute('aria-hidden', isPasswordHidden ? 'false' : 'true');
  }

  toggle.setAttribute('data-password-visible', isPasswordHidden ? 'false' : 'true');
  toggle.setAttribute('aria-pressed', isPasswordHidden ? 'false' : 'true');
  toggle.setAttribute(
    'aria-label',
    isPasswordHidden ? 'Show password' : 'Hide password'
  );
}

/**
 * 更新密码输入框 label 状态
 */
function updatePasswordLabelState(field, label, hasValue, isFocused) {
  if (!label) return;
  
  // 如果有值，label总是浮动（无论是否聚焦）
  if (hasValue) {
    label.classList.add('is-float');
  } else if (isFocused) {
    // 如果聚焦且无值，label也浮动（因为正在输入）
    label.classList.add('is-float');
  } else {
    // 如果无值且未聚焦，label回到中间
    label.classList.remove('is-float');
  }
}

/**
 * 更新密码图标显示状态
 */
function updatePasswordIconVisibility(field, toggle, errorIcon, hasValue, hasError, isFocused) {
  // 失焦且有错误：显示错误图标，隐藏切换按钮
  if (!isFocused && hasError) {
    if (errorIcon) {
      errorIcon.style.display = 'flex';
    }
    if (toggle) {
      toggle.style.display = 'none';
    }
    return;
  }
  
  // 聚焦且有值：显示切换按钮，隐藏错误图标
  if (isFocused && hasValue) {
    if (errorIcon) {
      errorIcon.style.display = 'none';
    }
    if (toggle) {
      syncPasswordToggleIcon(field, toggle);
      toggle.style.display = 'flex';
    }
    return;
  }
  
  // 其他情况：隐藏所有图标
  if (toggle) {
    toggle.style.display = 'none';
  }
  if (errorIcon) {
    errorIcon.style.display = 'none';
  }
}

/**
 * 处理密码输入框聚焦事件
 */
function handlePasswordFocus(field, label, toggle, errorIcon) {
  const value = field.value.trim();
  const hasValue = value.length > 0;
  
  // 聚焦时，如果有值则浮动，如果无值也浮动（因为正在输入）
  updatePasswordLabelState(field, label, hasValue, true);
  
  // 聚焦时只隐藏当前密码框的错误信息
  clearPasswordError(field);
  
  // 聚焦时也清除确认密码框的错误状态
  clearConfirmPasswordError(field);
  
  // 更新图标显示状态
  updatePasswordIconVisibility(field, toggle, errorIcon, hasValue, false, true);
  
  // 确保聚焦状态正确
  field.classList.add('component-password-input__field--focused');
}

/**
 * 处理密码输入框失焦事件
 */
function handlePasswordBlur(field, label, toggle, errorIcon) {
  let value = field.value.trim();
  const hasValue = value.length > 0;
  
  // XSS 检测：如果检测到脚本，显示错误（不清空内容）
  if (hasValue && passwordInputDetectXSS(value)) {
    // 显示 XSS 错误
    setPasswordError(field, 'Invalid input detected');
    
    // 更新状态
    updatePasswordLabelState(field, label, hasValue, false);
    field.classList.remove('component-password-input__field--focused');
    updatePasswordIconVisibility(field, toggle, errorIcon, hasValue, true, false);
    
    return;
  }
  
  // SQL 注入检测：如果检测到 SQL 注入模式，显示错误（不清空内容）
  if (hasValue && passwordInputDetectSQLInjection(value)) {
    // 显示 SQL 注入错误
    setPasswordError(field, 'Invalid input detected');
    
    // 更新状态
    updatePasswordLabelState(field, label, hasValue, false);
    field.classList.remove('component-password-input__field--focused');
    updatePasswordIconVisibility(field, toggle, errorIcon, hasValue, true, false);
    
    return;
  }
  
  // 失焦时，如果没有值，label回到中间
  updatePasswordLabelState(field, label, hasValue, false);
  
  // 移除聚焦状态
  field.classList.remove('component-password-input__field--focused');
  
  // 密码验证逻辑 - 跳过no-validation类
  const input = field.closest('.component-password-input');
  const hasNoValidation = input && (input.classList.contains('no-validation') || input.classList.contains('NoValidation'));
  
  if (!hasNoValidation) {
    const password = field.value;
    
    // 检测是否为移动设备
    const isMobile = window.innerWidth <= 768;
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    // 验证密码的函数
    function validateCurrentPassword() {
      const validation = validatePassword(password);
      
      // 如果有值但验证失败，设置错误状态
      if (password.length > 0 && !validation.isValid) {
        setPasswordError(field, 'Password does not meet requirements');
      } else {
        clearPasswordError(field);
      }
      
      // 密码框失焦后，检查确认密码框是否有错误并显示
      checkAndShowConfirmPasswordError(field);
    }
    
    if (isMobile && isTouchDevice) {
      // 移动设备：延迟显示错误，等待软键盘完全收起
      setTimeout(() => {
        // 再次检查输入框是否仍然失焦（防止在延迟期间重新聚焦）
        if (field !== document.activeElement) {
          validateCurrentPassword();
        }
      }, 250); // 等待250ms，确保软键盘完全收起
    } else {
      // 桌面设备：立即显示错误
      validateCurrentPassword();
    }
  } else {
    clearPasswordError(field);
    
    // 即使跳过验证，也要检查确认密码框的错误状态
    checkAndShowConfirmPasswordError(field);
  }
}

/**
 * 清除确认密码框的错误状态
 */
function clearConfirmPasswordError(passwordField) {
  // 查找确认密码字段
  const input = passwordField.closest('.component-password-input');
  if (!input) return;
  
  const parentContainer = input.closest('.fill-information-page__input-container') ||
                         input.closest('.setup-password-page__input-container') ||
                         input.closest('.login-password-page__input-container') ||
                         input.parentElement;
  
  if (!parentContainer) return;
  
  // 查找确认密码字段
  const confirmPasswordInput = parentContainer.querySelector('.component-password-input.confirm-password');
  if (!confirmPasswordInput) return;
  
  const confirmPasswordField = confirmPasswordInput.querySelector('.component-password-input__field');
  if (!confirmPasswordField) return;
  
  // 清除确认密码框的错误状态
  clearPasswordError(confirmPasswordField);
}

/**
 * 检查并显示确认密码框的错误状态
 */
function checkAndShowConfirmPasswordError(passwordField) {
  // 查找确认密码字段
  const input = passwordField.closest('.component-password-input');
  if (!input) return;
  
  const parentContainer = input.closest('.fill-information-page__input-container') ||
                         input.closest('.setup-password-page__input-container') ||
                         input.closest('.login-password-page__input-container') ||
                         input.parentElement;
  
  if (!parentContainer) return;
  
  // 查找确认密码字段
  const confirmPasswordInput = parentContainer.querySelector('.component-password-input.confirm-password');
  if (!confirmPasswordInput) return;
  
  const confirmPasswordField = confirmPasswordInput.querySelector('.component-password-input__field');
  if (!confirmPasswordField) return;
  
  const password = passwordField.value;
  const confirmPassword = confirmPasswordField.value;
  
  // 如果确认密码框有值且不匹配，显示错误状态
  if (confirmPassword.length > 0 && password.length > 0 && confirmPassword !== password) {
    setPasswordError(confirmPasswordField, 'Password does not match, please try again.');
  } else if (confirmPassword.length > 0 && password.length > 0 && confirmPassword === password) {
    clearPasswordError(confirmPasswordField);
  }
}

/**
 * 检测是否是确认密码字段
 */
function isConfirmPasswordField(input, field) {
  // 主要方法：通过CSS类名检测（通用confirm-password类）
  if (input.closest('.confirm-password') !== null) {
    return true;
  }
  
  // 备用方法：通过label文本检测
  const label = input.querySelector('.component-password-input__label');
  if (label) {
    const labelText = label.textContent.toLowerCase().trim();
    if (labelText.includes('confirm') || labelText.includes('确认')) {
      return true;
    }
  }
  
  return false;
}

/**
 * 获取确认密码字段
 */
function getConfirmPasswordField(passwordField) {
  const input = passwordField.closest('.component-password-input');
  if (!input) return null;
  
  const parentContainer = input.closest('.fill-information-page__input-container') ||
                         input.closest('.setup-password-page__input-container') ||
                         input.closest('.login-password-page__input-container') ||
                         input.parentElement;
  if (!parentContainer) return null;
  
  // 查找确认密码字段
  const confirmPasswordInputs = parentContainer.querySelectorAll('.component-password-input.confirm-password');
  if (confirmPasswordInputs.length > 0) {
    return confirmPasswordInputs[0].querySelector('.component-password-input__field');
  }
  
  return null;
}

/**
 * 重新验证确认密码字段
 */
function revalidateConfirmPasswordField(passwordField) {
  const password = passwordField.value;
  
  // 获取确认密码字段
  const confirmPasswordField = getConfirmPasswordField(passwordField);
  if (confirmPasswordField && confirmPasswordField.value.length > 0) {
    const confirmPassword = confirmPasswordField.value;
    
    // 只有当确认密码字段失焦时才验证匹配性
    // 在输入过程中不显示错误状态，避免影响用户体验
    if (confirmPasswordField !== document.activeElement) {
      if (confirmPassword !== password) {
        setPasswordError(confirmPasswordField, 'Password does not match, please try again.');
      } else {
        clearPasswordError(confirmPasswordField);
      }
    }
  }
}

/**
 * 处理确认密码输入框失焦事件
 */
function handleConfirmPasswordBlur(field, label, toggle, errorIcon) {
  let value = field.value.trim();
  const hasValue = value.length > 0;
  
  // XSS 检测：如果检测到脚本，显示错误（不清空内容）
  if (hasValue && passwordInputDetectXSS(value)) {
    // 显示 XSS 错误
    setPasswordError(field, 'Invalid input detected');
    
    // 更新状态
    updatePasswordLabelState(field, label, hasValue, false);
    field.classList.remove('component-password-input__field--focused');
    updatePasswordIconVisibility(field, toggle, errorIcon, hasValue, true, false);
    
    return;
  }
  
  // SQL 注入检测：如果检测到 SQL 注入模式，显示错误（不清空内容）
  if (hasValue && passwordInputDetectSQLInjection(value)) {
    // 显示 SQL 注入错误
    setPasswordError(field, 'Invalid input detected');
    
    // 更新状态
    updatePasswordLabelState(field, label, hasValue, false);
    field.classList.remove('component-password-input__field--focused');
    updatePasswordIconVisibility(field, toggle, errorIcon, hasValue, true, false);
    
    return;
  }
  
  // 失焦时，如果没有值，label回到中间
  updatePasswordLabelState(field, label, hasValue, false);
  
  // 移除聚焦状态
  field.classList.remove('component-password-input__field--focused');
  
  // 确认密码验证逻辑
  const confirmPassword = field.value;
  
  // 查找对应的密码字段
  let passwordField = null;
  const input = field.closest('.component-password-input');
  if (input) {
    const parentContainer = input.closest('.fill-information-page__input-container') ||
                           input.closest('.setup-password-page__input-container') ||
                           input.closest('.login-password-page__input-container') ||
                           input.parentElement;
    if (parentContainer) {
      // 查找同级的密码字段（不是确认密码字段）
      const passwordInputs = parentContainer.querySelectorAll('.component-password-input:not(.confirm-password)');
      if (passwordInputs.length > 0) {
        passwordField = passwordInputs[0].querySelector('.component-password-input__field');
      }
    }
  }
  
  const password = passwordField ? passwordField.value : '';
  
  // 检测是否为移动设备
  const isMobile = window.innerWidth <= 768;
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  // 验证确认密码的函数
  function validateCurrentConfirmPassword() {
    // 验证确认密码框本身
    if (confirmPassword.length > 0 && password.length > 0 && confirmPassword !== password) {
      setPasswordError(field, 'Password does not match, please try again.');
    } else {
      clearPasswordError(field);
    }
    
    // 同时检查主密码框的错误状态
    if (passwordField) {
      checkAndShowPasswordError(passwordField);
    }
  }
  
  if (isMobile && isTouchDevice) {
    // 移动设备：延迟显示错误，等待软键盘完全收起
    setTimeout(() => {
      // 再次检查输入框是否仍然失焦（防止在延迟期间重新聚焦）
      if (field !== document.activeElement) {
        validateCurrentConfirmPassword();
      }
    }, 200); // 等待200ms，确保软键盘完全收起
  } else {
    // 桌面设备：立即显示错误
    validateCurrentConfirmPassword();
  }
}

/**
 * 检查并显示主密码框的错误状态
 */
function checkAndShowPasswordError(passwordField) {
  const password = passwordField.value;
  const input = passwordField.closest('.component-password-input');
  const hasNoValidation = input && (input.classList.contains('no-validation') || input.classList.contains('NoValidation'));
  
  if (!hasNoValidation) {
    // 验证主密码框
    const validation = validatePassword(password);
    
    if (password.length > 0 && !validation.isValid) {
      setPasswordError(passwordField, 'Password does not meet requirements');
    } else {
      clearPasswordError(passwordField);
    }
  } else {
    clearPasswordError(passwordField);
  }
}

/**
 * 密码验证函数
 */
function validatePassword(password) {
  const hasMinLength = password.length >= 8;
  const hasLetter = /[a-zA-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const noSpace = !password.includes(' ');
  // 只允许英文字符、数字、特殊字符（除空格）：检查是否包含非法字符
  // 允许ASCII可打印字符（33-126）除空格(32)外
  const onlyValidChars = /^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]+$/.test(password);
  
  return {
    minLength: hasMinLength,
    hasLetter: hasLetter,
    hasNumber: hasNumber,
    noSpace: noSpace,
    onlyValidChars: onlyValidChars,
    isValid: hasMinLength && hasLetter && hasNumber && noSpace && onlyValidChars
  };
}

/**
 * 实时验证密码要求并更新提示文本
 */
function validatePasswordRequirements(field, setErrorState = true) {
  const password = field.value;
  const validation = validatePassword(password);
  
  // 查找密码要求提示元素
  const inputContainer = field.closest('.component-password-input');
  if (!inputContainer) return;
  
  // 查找父级容器中的密码要求提示
  const parentContainer = inputContainer.closest('.fill-information-page__input-container') || 
                         inputContainer.closest('.setup-password-page__input-container') ||
                         inputContainer.parentElement;
  
  if (!parentContainer) return;
  
  // 查找密码要求提示元素
  const requirementsContainer = parentContainer.querySelector('.fill-information-page__password-requirements') ||
                               parentContainer.querySelector('.setup-password-page__password-requirements');
  
  if (!requirementsContainer) return;
  
  const req1 = requirementsContainer.querySelector('#req1, [data-requirement="length"]');
  const req2 = requirementsContainer.querySelector('#req2, [data-requirement="alphanumeric"]');
  const req3 = requirementsContainer.querySelector('#req3, [data-requirement="nospace"]');
  const req4 = requirementsContainer.querySelector('#req4, [data-requirement="invalidinput"]');
  
  // 更新第一个要求：至少8个字符
  if (req1) {
    req1.classList.remove('valid', 'invalid', 'default');
    if (password.length === 0) {
      // 空值时重置到初始状态
      req1.classList.add('default');
      req1.style.color = 'var(--surface-on-surface-variant, #B3B3BA)';
    } else if (validation.minLength) {
      req1.classList.add('valid');
      req1.style.color = 'var(--primary-primary-mode-1, #ECFDAC)';
    } else {
      req1.classList.add('invalid');
      req1.style.color = 'var(--function-error-mode-1, #FFB4AB)';
    }
  }
  
  // 更新第二个要求：包含字母和数字
  if (req2) {
    req2.classList.remove('valid', 'invalid', 'default');
    if (password.length === 0) {
      // 空值时重置到初始状态
      req2.classList.add('default');
      req2.style.color = 'var(--surface-on-surface-variant, #B3B3BA)';
    } else if (validation.hasLetter && validation.hasNumber) {
      req2.classList.add('valid');
      req2.style.color = 'var(--primary-primary-mode-1, #ECFDAC)';
    } else {
      req2.classList.add('invalid');
      req2.style.color = 'var(--function-error-mode-1, #FFB4AB)';
    }
  }
  
  // 更新第三个要求：无空格
  if (req3) {
    req3.classList.remove('valid', 'invalid', 'default');
    if (password.length === 0) {
      // 空值时重置到初始状态
      req3.classList.add('default');
      req3.style.color = 'var(--surface-on-surface-variant, #B3B3BA)';
    } else if (validation.noSpace) {
      req3.classList.add('valid');
      req3.style.color = 'var(--primary-primary-mode-1, #ECFDAC)';
    } else {
      req3.classList.add('invalid');
      req3.style.color = 'var(--function-error-mode-1, #FFB4AB)';
    }
  }

  if (req4) {
    const hasValue = password.length > 0;
    const isInvalidInput = hasValue && (passwordInputDetectXSS(password) || passwordInputDetectSQLInjection(password));
    req4.style.display = isInvalidInput ? '' : 'none';
    if (isInvalidInput) {
      req4.classList.remove('valid', 'default');
      req4.classList.add('invalid');
      req4.style.color = 'var(--function-error-mode-1, #FFB4AB)';
    } else {
      req4.classList.remove('valid', 'invalid', 'default');
      req4.style.color = '';
    }
  }
  
  // 根据参数决定是否设置错误状态
  if (setErrorState && !validation.isValid && password.length > 0) {
    // 只有在需要设置错误状态且密码不符合要求时才设置错误
    setPasswordError(field, 'Password does not meet requirements');
  } else if (setErrorState && validation.isValid) {
    // 如果密码符合要求，清除错误状态
    clearPasswordError(field);
  }
}

/**
 * 设置密码错误状态
 */
function setPasswordError(field, errorMessage) {
  if (!field) {
    return;
  }
  const localizedErrorMessage = window.localizeFrontendText ? window.localizeFrontendText(errorMessage) : errorMessage;
  
  // 添加错误类
  field.classList.add('component-password-input__field--error');
  
  // 设置 aria-invalid 属性
  field.setAttribute('aria-invalid', 'true');
  
  // 查找错误信息元素
  const input = field.closest('.component-password-input');
  const errorElement = input.querySelector('.component-password-input__error');
  
  if (errorElement) {
    // 检查是否已经有相同的错误信息
    const currentText = errorElement.textContent.trim();
    if (currentText !== localizedErrorMessage) {
      if (window.setInputErrorMessage) {
        window.setInputErrorMessage(errorElement, localizedErrorMessage);
      } else {
        errorElement.textContent = localizedErrorMessage;
      }
    } else if (window.syncInputErrorLineState) {
      window.syncInputErrorLineState(errorElement);
    }
    errorElement.style.display = 'flex';
  }

  input.classList.add('component-password-input--has-visible-error');
  if (window.syncFormInputErrorState) {
    window.syncFormInputErrorState(input);
  }
  
  // 更新图标显示状态
  const toggle = input.querySelector('.component-password-input__toggle');
  const errorIcon = input.querySelector('.component-password-input__error-icon');
  
  updatePasswordIconVisibility(field, toggle, errorIcon, field.value.trim().length > 0, true, field === document.activeElement);
}

/**
 * 清除密码错误状态
 */
function clearPasswordError(field) {
  if (!field) return;
  
  // 移除错误状态类
  field.classList.remove('component-password-input__field--error');
  
  // 移除 aria-invalid 属性
  field.removeAttribute('aria-invalid');
  
  // 隐藏错误信息
  const input = field.closest('.component-password-input');
  const errorElement = input.querySelector('.component-password-input__error');
  if (errorElement) {
    if (window.clearInputErrorMessage) {
      window.clearInputErrorMessage(errorElement);
    }
    errorElement.style.display = 'none';
  }

  input.classList.remove('component-password-input--has-visible-error');
  if (window.syncFormInputErrorState) {
    window.syncFormInputErrorState(input);
  }
  
  // 更新图标显示状态
  const toggle = input.querySelector('.component-password-input__toggle');
  const errorIcon = input.querySelector('.component-password-input__error-icon');
  
  updatePasswordIconVisibility(field, toggle, errorIcon, field.value.trim().length > 0, false, field === document.activeElement);
}

/**
 * 清除密码输入框内容
 */
function clearPasswordInput(field) {
  if (!field) return;
  
  // 清除输入框值
  field.value = '';
  
  // 移除错误状态
  field.classList.remove('component-password-input__field--error');
  
  // 获取相关元素
  const input = field.closest('.component-password-input');
  input.classList.remove('component-password-input--has-visible-error');
  if (window.syncFormInputErrorState) {
    window.syncFormInputErrorState(input);
  }
  const label = input.querySelector('.component-password-input__label');
  const toggle = input.querySelector('.component-password-input__toggle');
  const errorIcon = input.querySelector('.component-password-input__error-icon');
  
  // 更新状态
  updatePasswordInputState(field, label, toggle, errorIcon);
  
  // 聚焦到输入框
  field.focus();
  
  // 触发自定义事件
  const clearEvent = new CustomEvent('password:clear', {
    detail: { field: field }
  });
  field.dispatchEvent(clearEvent);
}

// 导出函数供外部使用
window.PasswordInputComponent = {
  init: initPasswordInputs,
  setError: setPasswordError,
  clearError: clearPasswordError,
  clearInput: clearPasswordInput,
  updateInputState: updatePasswordInputState,
  updateIconVisibility: updatePasswordIconVisibility,
  validatePassword: validatePassword,
  validatePasswordRequirements: validatePasswordRequirements
};

// 页面加载时自动初始化
document.addEventListener('DOMContentLoaded', function() {
  initPasswordInputs();
});

// 立即执行一次，以防DOMContentLoaded已经触发
if (document.readyState === 'loading') {
  // Document still loading, waiting for DOMContentLoaded
} else {
  initPasswordInputs();
}

// 窗口大小改变时重新初始化（处理设备切换）
window.addEventListener('resize', function() {
  // 防抖处理
  clearTimeout(window.passwordInputResizeTimeout);
  window.passwordInputResizeTimeout = setTimeout(() => {
    initPasswordInputs();
  }, 100);
}); 
